// Импорт необходимых библиотек и компонентов
import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles, Volume2, VolumeX } from "lucide-react";
import { Character } from "./Character";

import { PowerUp, PowerUpType } from "./PowerUp";
import { GameLevelDisplay, GAME_LEVELS, GameLevel } from "./GameLevel";
import { MobileControls } from "./MobileControls";
import { playCollectSound, playPowerUpSound, BackgroundMusic } from "./GameAudio";
import { useMobile } from "@/hooks/useMobile";

// Интерфейс для летающих сердечек
interface FloatingHeart {
  id: number;           // Уникальный идентификатор
  x: number;            // Позиция по горизонтали
  y: number;            // Позиция по вертикали
  collected: boolean;   // Собрано ли сердечко
  type: 'normal' | 'golden' | 'rainbow';  // Тип сердечка
  value: number;        // Количество очков за сердечко
}

// Интерфейс для позиции персонажа
interface Position {
  x: number;  // Координата X
  y: number;  // Координата Y
}

// Интерфейс для активных бонусов
interface ActivePowerUp {
  type: PowerUpType['type'];  // Тип бонуса
  endTime: number;            // Время окончания действия
}

// Основной компонент расширенной игры
export const EnhancedHeartGame = () => {
  // Состояния игры
  const [hearts, setHearts] = useState<FloatingHeart[]>([]);        // Массив сердечек на экране
  const [powerUps, setPowerUps] = useState<PowerUpType[]>([]);      // Массив бонусов на экране
  const [score, setScore] = useState(0);                            // Текущий счет игрока
  const [currentLevel, setCurrentLevel] = useState(0);              // Текущий уровень
  const [gameComplete, setGameComplete] = useState(false);          // Завершена ли игра
  const [gameStarted, setGameStarted] = useState(false);            // Начата ли игра
  const [showSparkles, setShowSparkles] = useState(false);          // Показывать ли блестки
  const [characterPosition, setCharacterPosition] = useState<Position>({ x: 100, y: 100 }); // Позиция персонажа

  // Дополнительные состояния для улучшенного геймплея
  const [activePowerUps, setActivePowerUps] = useState<ActivePowerUp[]>([]);  // Активные бонусы
  const [screenShake, setScreenShake] = useState(false);                      // Тряска экрана
  const [timeLeft, setTimeLeft] = useState<number | undefined>();             // Оставшееся время
  const [soundEnabled, setSoundEnabled] = useState(true);                     // Включен ли звук
  const [combo, setCombo] = useState(0);                                      // Текущее комбо
  const [comboTimer, setComboTimer] = useState<number | null>(null);          // Таймер комбо
  const [achievements, setAchievements] = useState<string[]>([]);             // Достижения игрока
  
  // Определение мобильного устройства и текущего уровня
  const isMobile = useMobile();
  const level = GAME_LEVELS[currentLevel];
  const gameContainerRef = useRef<HTMLDivElement>(null);  // Ссылка на контейнер игры

  // Функция обработки нажатий клавиш для движения персонажа
  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (!gameStarted || gameComplete) return;  // Если игра не активна - не обрабатываем
    
    const moveDistance = 20;  // Расстояние перемещения за одно нажатие
    setCharacterPosition(prev => {
      let newX = prev.x;
      let newY = prev.y;
      
      // Определяем направление движения по нажатой клавише
      switch (e.key.toLowerCase()) {
        case 'w':
        case 'arrowup':
          newY -= moveDistance;
          break;
        case 's':
        case 'arrowdown':
          newY += moveDistance;
          break;
        case 'a':
        case 'arrowleft':
          newX -= moveDistance;
          break;
        case 'd':
        case 'arrowright':
          newX += moveDistance;
          break;
        default:
          return prev;  // Если клавиша не распознана - не двигаемся
      }
      
      // Ограничиваем движение границами экрана
      const maxX = window.innerWidth - 80;
      const maxY = window.innerHeight - 80;
      
      return {
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY))
      };
    });
  }, [gameStarted, gameComplete]);

  // Функция генерации нового сердечка
  const generateHeart = useCallback(() => {
    // Определяем тип сердечка: 10% золотые, 5% радужные, остальные обычные
    const heartType = Math.random() < 0.1 ? 'golden' : Math.random() < 0.05 ? 'rainbow' : 'normal';
    // Устанавливаем ценность в зависимости от типа
    const value = heartType === 'rainbow' ? 5 : heartType === 'golden' ? 3 : 1;
    
    // Создаем новое сердечко со случайными координатами
    const newHeart: FloatingHeart = {
      id: Math.random(),                                        // Уникальный ID
      x: Math.random() * (window.innerWidth - 100),           // Случайная позиция X
      y: Math.random() * (window.innerHeight - 200) + 100,    // Случайная позиция Y
      collected: false,                                         // Изначально не собрано
      type: heartType,                                         // Тип сердечка
      value                                                    // Ценность
    };
    return newHeart;
  }, []);

  // Функция генерации нового бонуса
  const generatePowerUp = useCallback(() => {
    // Доступные типы бонусов
    const types: PowerUpType['type'][] = ['speed', 'magnet', 'shield', 'time'];
    // Выбираем случайный тип
    const type = types[Math.floor(Math.random() * types.length)];
    
    // Создаем новый бонус
    return {
      id: Math.random().toString(),                           // Уникальный ID
      type,                                                   // Тип бонуса
      x: Math.random() * (window.innerWidth - 100),         // Случайная позиция X
      y: Math.random() * (window.innerHeight - 200) + 100,  // Случайная позиция Y
      collected: false                                        // Изначально не собран
    };
  }, []);

  // Функция запуска новой игры
  const startGame = () => {
    // Сбрасываем все состояния игры
    setGameStarted(true);        // Помечаем игру как начатую
    setScore(0);                 // Обнуляем счет
    setCurrentLevel(0);          // Начинаем с первого уровня
    setGameComplete(false);      // Игра не завершена
    setHearts([]);               // Очищаем сердечки
    setPowerUps([]);             // Очищаем бонусы
    setActivePowerUps([]);       // Очищаем активные бонусы
    setCombo(0);                 // Обнуляем комбо
    setAchievements([]);         // Очищаем достижения
    
    // Устанавливаем время для первого уровня
    const firstLevel = GAME_LEVELS[0];
    setTimeLeft(firstLevel.timeLimit);
    
    // Генерируем начальные сердечки
    const initialHearts = Array.from({ length: 5 }, () => generateHeart());
    setHearts(initialHearts);
    
    // Фокусируемся на контейнере игры для обработки клавиатуры
    setTimeout(() => {
      gameContainerRef.current?.focus();
    }, 100);
  };

  // Функция проверки и разблокировки достижений
  const checkAchievements = useCallback(() => {
    // Проверяем достижение "Мастер комбо" (10+ комбо)
    if (combo >= 10 && !achievements.includes('combo_master')) {
      setAchievements(prev => [...prev, 'combo_master']);
    }
    // Проверяем достижение "Коллекционер сердец" (100+ очков)
    if (score >= 100 && !achievements.includes('heart_collector')) {
      setAchievements(prev => [...prev, 'heart_collector']);
    }
  }, [combo, score, achievements]);

  // Функция сбора сердечка
  const collectHeart = useCallback((heartId: number) => {
    setHearts(prev => {
      const heart = prev.find(h => h.id === heartId);
      if (!heart || heart.collected) return prev;  // Если сердечко не найдено или уже собрано

      // Немедленно помечаем как собранное и планируем удаление
      const updatedHearts = prev.map(h => 
        h.id === heartId ? { ...h, collected: true } : h
      );
      
      // Обновляем счет и комбо
      setScore(current => current + heart.value * (combo > 0 ? combo : 1));
      setCombo(current => current + 1);
      
      // Сбрасываем таймер комбо
      if (comboTimer) clearTimeout(comboTimer);
      const timer = setTimeout(() => setCombo(0), 2000);  // Комбо сбрасывается через 2 секунды
      setComboTimer(timer as any);
      
      // Убрали эффект частиц для предотвращения лагов
      
      // Тряска экрана для особых сердечек
      if (heart.type !== 'normal') {
        setScreenShake(true);
        requestAnimationFrame(() => {
          setTimeout(() => setScreenShake(false), 300);
        });
      }
      
      // Звуковой эффект
      if (soundEnabled) playCollectSound();
      
      // Удаляем собранное сердечко после анимации без блокировки
      requestAnimationFrame(() => {
        setTimeout(() => {
          setHearts(current => current.filter(h => h.id !== heartId));
        }, 300);  // Задержка для анимации исчезновения
      });

      // Проверяем достижения
      checkAchievements();
      
      return updatedHearts;
    });
  }, [combo, comboTimer, soundEnabled, checkAchievements]);

  // Функция сбора бонуса
  const collectPowerUp = useCallback((powerUpId: string) => {
    const powerUp = powerUps.find(p => p.id === powerUpId);
    if (!powerUp || powerUp.collected) return;  // Если не найден или уже собран

    // Помечаем бонус как собранный
    setPowerUps(prev => prev.map(p => 
      p.id === powerUpId ? { ...p, collected: true } : p
    ));

    // Активируем бонус на 10 секунд
    const endTime = Date.now() + 10000;
    setActivePowerUps(prev => [...prev.filter(p => p.type !== powerUp.type), { type: powerUp.type, endTime }]);

    // Воспроизводим звук сбора бонуса
    if (soundEnabled) playPowerUpSound();

    // Удаляем бонус после анимации
    setTimeout(() => {
      setPowerUps(prev => prev.filter(p => p.id !== powerUpId));
    }, 300);
  }, [powerUps, soundEnabled]);



  // Функция движения персонажа на мобильных устройствах
  const handleMobileMove = (direction: 'up' | 'down' | 'left' | 'right') => {
    const moveDistance = 20;  // Расстояние перемещения за один шаг
    setCharacterPosition(prev => {
      let newX = prev.x;
      let newY = prev.y;
      
      // Определяем новые координаты в зависимости от направления
      switch (direction) {
        case 'up': newY -= moveDistance; break;
        case 'down': newY += moveDistance; break;
        case 'left': newX -= moveDistance; break;
        case 'right': newX += moveDistance; break;
      }
      
      // Ограничиваем движение границами экрана
      const maxX = window.innerWidth - 80;
      const maxY = window.innerHeight - 80;
      
      return {
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY))
      };
    });
  };

  // Функция проверки столкновений с учетом эффектов бонусов
  const checkHeartCollision = useCallback(() => {
    // Проверяем активен ли бонус магнита
    const magnetActive = activePowerUps.some(p => p.type === 'magnet' && p.endTime > Date.now());
    const radius = magnetActive ? 80 : 40;  // Увеличенный радиус с магнитом
    
    // Проверяем столкновения с сердечками
    hearts.forEach(heart => {
      if (heart.collected) return;  // Пропускаем уже собранные
      
      // Вычисляем расстояние между персонажем и сердечком
      const distance = Math.sqrt(
        Math.pow(characterPosition.x + 32 - (heart.x + 24), 2) +
        Math.pow(characterPosition.y + 32 - (heart.y + 24), 2)
      );
      
      // Если расстояние меньше радиуса - собираем сердечко
      if (distance < radius) {
        collectHeart(heart.id);
      }
    });

    // Проверяем столкновения с бонусами
    powerUps.forEach(powerUp => {
      if (powerUp.collected) return;  // Пропускаем уже собранные
      
      // Вычисляем расстояние между персонажем и бонусом
      const distance = Math.sqrt(
        Math.pow(characterPosition.x + 32 - (powerUp.x + 16), 2) +
        Math.pow(characterPosition.y + 32 - (powerUp.y + 16), 2)
      );
      
      // Если расстояние меньше 40 пикселей - собираем бонус
      if (distance < 40) {
        collectPowerUp(powerUp.id);
      }
    });
  }, [hearts, powerUps, characterPosition, activePowerUps, collectHeart, collectPowerUp]);

  // Level progression and timing
  useEffect(() => {
    if (level && score >= level.targetScore && !gameComplete) {
      if (currentLevel < GAME_LEVELS.length - 1) {
        setCurrentLevel(prev => prev + 1);
        setTimeLeft(GAME_LEVELS[currentLevel + 1].timeLimit);
      } else {
        setGameComplete(true);
        setShowSparkles(true);
        setHearts([]);
        setPowerUps([]);
      }
    }
  }, [score, level, currentLevel, gameComplete]);

  // Таймер обратного отсчета
  useEffect(() => {
    // Если игра начата, не завершена и есть время - запускаем таймер
    if (gameStarted && !gameComplete && timeLeft !== undefined && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev ? prev - 1 : 0), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      // Время истекло - перезапускаем уровень
      setScore(0);
      setTimeLeft(level?.timeLimit);
    }
  }, [gameStarted, gameComplete, timeLeft, level]);

  // Генерация сердечек и бонусов
  useEffect(() => {
    // Если игра не начата, завершена или нет уровня - не генерируем объекты
    if (!gameStarted || gameComplete || !level) return;

    // Интервал для генерации сердечек
    const heartInterval = setInterval(() => {
      if (hearts.length < 8) {  // Максимум 8 сердечек на экране
        setHearts(prev => [...prev, generateHeart()]);
      }
    }, level.heartSpawnRate);  // Частота появления зависит от уровня

    // Интервал для генерации бонусов
    const powerUpInterval = setInterval(() => {
      // Максимум 2 бонуса, шанс появления зависит от уровня
      if (powerUps.length < 2 && Math.random() < level.powerUpChance) {
        setPowerUps(prev => [...prev, generatePowerUp()]);
      }
    }, 5000);  // Проверяем каждые 5 секунд

    // Очищаем интервалы при размонтировании
    return () => {
      clearInterval(heartInterval);
      clearInterval(powerUpInterval);
    };
  }, [gameStarted, gameComplete, hearts.length, powerUps.length, level, generateHeart, generatePowerUp]);

  // Оптимизированная проверка столкновений с requestAnimationFrame
  useEffect(() => {
    // Если игра не начата или завершена - не проверяем столкновения
    if (!gameStarted || gameComplete) return;
    
    let animationId: number;
    
    // Функция для непрерывной проверки столкновений
    const runCollisionCheck = () => {
      checkHeartCollision();  // Проверяем столкновения
      animationId = requestAnimationFrame(runCollisionCheck);  // Планируем следующую проверку
    };
    
    // Запускаем первую проверку
    animationId = requestAnimationFrame(runCollisionCheck);
    
    // Отменяем анимацию при размонтировании
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [gameStarted, gameComplete, checkHeartCollision]);

  // Очистка истекших бонусов
  useEffect(() => {
    const now = Date.now();  // Текущее время
    // Удаляем бонусы, время действия которых истекло
    setActivePowerUps(prev => prev.filter(p => p.endTime > now));
  }, [activePowerUps]);

  // Обработчики событий клавиатуры
  useEffect(() => {
    const gameContainer = gameContainerRef.current;
    if (gameContainer) {
      // Добавляем обработчик нажатий клавиш
      gameContainer.addEventListener('keydown', handleKeyPress);
      // Удаляем обработчик при размонтировании
      return () => gameContainer.removeEventListener('keydown', handleKeyPress);
    }
  }, [handleKeyPress]);

  // Движение персонажа за мышью
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Если игра не начата или завершена - не обрабатываем движение
      if (!gameStarted || gameComplete) return;
      
      // Получаем границы игрового контейнера
      const rect = gameContainerRef.current?.getBoundingClientRect();
      if (rect) {
        // Вычисляем позицию персонажа относительно курсора (центрируем)
        const x = e.clientX - rect.left - 32;
        const y = e.clientY - rect.top - 32;
        
        // Ограничиваем движение границами экрана
        setCharacterPosition({
          x: Math.max(0, Math.min(x, window.innerWidth - 64)),
          y: Math.max(0, Math.min(y, window.innerHeight - 64))
        });
      }
    };
    
    const gameContainer = gameContainerRef.current;
    if (gameContainer) {
      // Добавляем обработчик движения мыши
      gameContainer.addEventListener('mousemove', handleMouseMove);
      // Удаляем обработчик при размонтировании
      return () => gameContainer.removeEventListener('mousemove', handleMouseMove);
    }
  }, [gameStarted, gameComplete]);

  // Эффект тряски экрана
  useEffect(() => {
    if (screenShake) {
      // Отключаем тряску через 200мс
      const timer = setTimeout(() => setScreenShake(false), 200);
      return () => clearTimeout(timer);
    }
  }, [screenShake]);

  // Экран начала игры
  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-gradient-background flex items-center justify-center p-4">
        <div className="text-center space-y-8">
          <div className="space-y-4">
            {/* Иконка сердца с анимацией */}
            <Heart className="mx-auto text-primary w-20 h-20 animate-heartbeat" />
            {/* Заголовок игры */}
            <h1 className="text-4xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              Игра для моей любимой
            </h1>
            {/* Описание игры */}
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              Пройди все уровни и собери сердечки!
            </p>
          </div>
          {/* Кнопка запуска игры */}
          <Button 
            onClick={startGame}
            size="lg"
            className="bg-gradient-heart hover:scale-105 transition-transform shadow-heart-glow animate-glow-pulse"
          >
            <Heart className="mr-2 h-5 w-5" />
            Начать приключение
          </Button>
        </div>
      </div>
    );
  }

  // Экран завершения игры
  if (gameComplete) {
    return (
      <div className="min-h-screen bg-gradient-background flex items-center justify-center p-4 relative overflow-hidden">
        {/* Анимированные блестки для эффекта победы */}
        {showSparkles && (
          <>
            {Array.from({ length: 30 }).map((_, i) => (
              <Sparkles
                key={i}
                className="absolute text-accent animate-sparkle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  fontSize: `${Math.random() * 30 + 15}px`,
                }}
              />
            ))}
          </>
        )}
        
        <div className="text-center space-y-8 z-10">
          {/* Большое сердце с анимацией */}
          <Heart className="mx-auto text-primary w-40 h-40 animate-heartbeat shadow-heart-glow animate-glow-pulse" />
          <div className="space-y-4">
            {/* Главное сообщение любви */}
            <h1 className="text-7xl font-bold bg-gradient-romantic bg-clip-text text-transparent animate-bounce-in">
              Я люблю тебя
            </h1>
            {/* Дополнительное сообщение */}
            <p className="text-3xl text-accent font-medium">
              Я люблю тебя ♥
            </p>
            {/* Финальный счет */}
            <p className="text-xl text-muted-foreground max-w-md mx-auto">
              Финальный счет: {score} сердечек
            </p>
            {/* Блок достижений */}
            {achievements.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-semibold">Достижения:</h3>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {achievements.map(achievement => (
                    <span key={achievement} className="bg-accent/20 px-3 py-1 rounded-full text-sm">
                      {achievement === 'combo_master' ? '🔥 Мастер комбо' : '💎 Коллекционер сердец'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Кнопка перезапуска игры */}
          <Button 
            onClick={() => {
              setGameStarted(false);
              setGameComplete(false);
              setShowSparkles(false);
              setScore(0);
              setCurrentLevel(0);
            }}
            variant="secondary"
            size="lg"
            className="mt-8 animate-bounce-in"
          >
            <Heart className="mr-2 h-4 w-4" />
            Играть снова
          </Button>
        </div>
      </div>
    );
  }

  return (
    // Основной игровой контейнер
    <div 
      ref={gameContainerRef}
      className={`min-h-screen ${level?.backgroundTheme || 'bg-gradient-background'} relative overflow-hidden ${screenShake ? 'animate-shake' : ''}`}
      tabIndex={0}
      style={{ outline: 'none' }}
      onFocus={() => {}}
    >
      {/* Персонаж с улучшенным движением */}
      <Character 
        position={characterPosition} 
        onMove={setCharacterPosition}
      />

      {/* Отображение информации о уровне */}
      {level && (
        <GameLevelDisplay 
          level={level} 
          score={score} 
          timeLeft={timeLeft}
        />
      )}

      {/* Фоновая музыка */}
      <BackgroundMusic isPlaying={gameStarted && soundEnabled} volume={0.15} />
      
      {/* Переключатель звука */}
      <div className="fixed top-4 right-4 z-10">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSoundEnabled(!soundEnabled)}
        >
          {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </Button>
      </div>

      {/* Отображение комбо */}
      {combo > 1 && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-accent text-accent-foreground px-4 py-2 rounded-full font-bold animate-bounce-in">
            🔥 Комбо x{combo}
          </div>
        </div>
      )}

      {/* Отображение активных бонусов */}
      {activePowerUps.length > 0 && (
        <div className="fixed bottom-4 left-4 bg-card rounded-lg p-3 shadow-lg z-10">
          <div className="text-sm font-semibold mb-2">Активные силы:</div>
          {activePowerUps.map((powerUp, index) => (
            <div key={index} className="text-xs">
              {powerUp.type === 'speed' && '⚡ Скорость'}
              {powerUp.type === 'magnet' && '🧲 Магнит'}
              {powerUp.type === 'shield' && '🛡️ Щит'}
              {powerUp.type === 'time' && '⏰ Время'}
            </div>
          ))}
        </div>
      )}

      {/* Мобильные элементы управления */}
      <MobileControls onMove={handleMobileMove} isMobile={isMobile} />

      {/* Информация об управлении для десктопа */}
      {!isMobile && (
        <div className="fixed bottom-4 right-4 bg-card rounded-lg p-4 shadow-lg z-10 max-w-xs">
          <div className="text-center space-y-2">
            <p className="text-sm font-semibold">Управление:</p>
            <div className="grid grid-cols-3 gap-1 w-24 mx-auto">
              <div></div>
              <div className="bg-muted rounded px-2 py-1 text-xs font-mono">W</div>
              <div></div>
              <div className="bg-muted rounded px-2 py-1 text-xs font-mono">A</div>
              <div className="bg-muted rounded px-2 py-1 text-xs font-mono">S</div>
              <div className="bg-muted rounded px-2 py-1 text-xs font-mono">D</div>
            </div>
          </div>
        </div>
      )}

      {/* Летающие сердечки разных типов */}
      {hearts.map((heart) => (
        <div
          key={heart.id}
          className={`absolute cursor-pointer transition-all duration-300 ${
            heart.collected ? 'scale-150 opacity-0' : 'hover:scale-110'
          } ${heart.type === 'golden' ? 'animate-glow-pulse' : ''}`}
          style={{ left: heart.x, top: heart.y }}
          onClick={() => !heart.collected && collectHeart(heart.id)}
        >
          {/* Иконка сердца с цветом в зависимости от типа */}
          <Heart 
            className={`w-12 h-12 animate-float drop-shadow-lg ${
              heart.type === 'rainbow' ? 'text-purple-500' :
              heart.type === 'golden' ? 'text-yellow-500' : 'text-primary'
            }`}
            fill="currentColor"
          />
          {/* Индикатор ценности сердца */}
          {heart.value > 1 && (
            <div className="absolute -top-2 -right-2 bg-accent text-accent-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
              {heart.value}
            </div>
          )}
        </div>
      ))}

      {/* Бонусы (усиления) */}
      {powerUps.map((powerUp) => (
        <PowerUp
          key={powerUp.id}
          powerUp={powerUp}
          onCollect={() => collectPowerUp(powerUp.id)}
        />
      ))}

      {/* Уведомления о достижениях */}
      {achievements.length > 0 && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
          {achievements.slice(-1).map((achievement, index) => (
            <div
              key={index}
              className="bg-accent text-accent-foreground px-6 py-3 rounded-lg shadow-lg animate-bounce-in"
            >
              🏆 Достижение: {achievement}
            </div>
          ))}
        </div>
      )}

    </div>
  );
};